import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { RiShieldKeyholeLine } from '@/shared/icons/remix'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { ApiError } from '@/api/http/types'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  useForceResetOtpVerify,
  useForceResetOtpResend,
} from '../api'
import {
  FORCE_RESET_SESSION_ID_STATE_KEY,
  FORCE_RESET_MASKED_EMAIL_STATE_KEY,
  FORCE_RESET_FROM_STATE_KEY,
  SP_OTP_EXPIRED_MESSAGE,
  SP_NETWORK_ERROR_MESSAGE,
  isNetworkFailureError,
  isOtpDeliveryFailure,
  resolveOtpInlineErrorMessage,
  resolveOtpSendFailureMessage,
} from '../lib'
import { lockoutFromOtpApiError } from '../lib/lockout'
import {
  AuthCard,
  BackToLoginButton,
  LockoutCountdown,
  OtpInput,
} from '../components'
import { useLockoutRemaining } from '../hooks/useLockoutRemaining'
import { headingSx, primaryButtonSx, subtitleSx } from '../styles/authFormStyles'
import { loginFieldErrorSx } from '../styles/loginSplitStyles'
import { palettePrimaryMain } from '@/theme/designTokens'

const RESEND_COOLDOWN_SECONDS = 120

const LOCKOUT_MESSAGE =
  'Too many incorrect attempts. Please wait 2 minutes before trying again.'

export function ForceResetOtpPage() {
  useDocumentTitle('Verify Identity')
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state as Record<string, unknown> | null) ?? {}
  const sessionId =
    typeof state[FORCE_RESET_SESSION_ID_STATE_KEY] === 'string'
      ? state[FORCE_RESET_SESSION_ID_STATE_KEY]
      : null
  const maskedEmail =
    typeof state[FORCE_RESET_MASKED_EMAIL_STATE_KEY] === 'string'
      ? state[FORCE_RESET_MASKED_EMAIL_STATE_KEY]
      : null
  const from = state[FORCE_RESET_FROM_STATE_KEY] as { pathname?: string; search?: string } | null | undefined

  const verifyOtp = useForceResetOtpVerify()
  const resendOtp = useForceResetOtpResend()
  const { showToast } = useToast()

  const [code, setCode] = useState('')
  const [currentSessionId, setCurrentSessionId] = useState(sessionId)
  const [serverError, setServerError] = useState<string | null>(null)
  const [lockoutUntilMs, setLockoutUntilMs] = useState<number | null>(null)
  const [resendCooldownUntilMs, setResendCooldownUntilMs] = useState<number | null>(null)
  const lockoutRemaining = useLockoutRemaining(lockoutUntilMs)
  const resendRemaining = useLockoutRemaining(resendCooldownUntilMs)
  const isLockedOut =
    lockoutUntilMs != null &&
    (lockoutRemaining > 0 || lockoutUntilMs > Date.now())
  const resendDisabled = resendRemaining > 0 || resendOtp.isPending || isLockedOut

  useEffect(() => {
    if (!currentSessionId) {
      navigate('/login', { replace: true })
    }
  }, [currentSessionId, navigate])

  useEffect(() => {
    if (lockoutUntilMs == null || lockoutRemaining > 0) return
    if (Date.now() < lockoutUntilMs) return
    setLockoutUntilMs(null)
    setServerError(null)
    setCode('')
  }, [lockoutUntilMs, lockoutRemaining])

  if (!currentSessionId) return null

  const handleVerify = () => {
    if (code.length !== 6 || isLockedOut) return
    setServerError(null)
    verifyOtp.mutate(
      { sessionId: currentSessionId, code },
      {
        onSuccess: () => {
          useSessionStore.getState().setRequiresForceReset(true)
          navigate('/force-reset/password', {
            replace: true,
            state: { [FORCE_RESET_FROM_STATE_KEY]: from ?? null },
          })
        },
        onError: (error) => {
          if (isNetworkFailureError(error)) {
            showToast(SP_NETWORK_ERROR_MESSAGE, 'error')
            return
          }
          if (error instanceof ApiError) {
            const lockout = lockoutFromOtpApiError(error)
            if (lockout) {
              setLockoutUntilMs(lockout.lockedUntilMs)
              setServerError(error.message || LOCKOUT_MESSAGE)
              setCode('')
              return
            }
            if (error.status === 410) {
              setServerError(error.message || SP_OTP_EXPIRED_MESSAGE)
              setCode('')
              setResendCooldownUntilMs(null)
              return
            }
            setServerError(resolveOtpInlineErrorMessage(error))
            return
          }
          setServerError(resolveOtpInlineErrorMessage(error))
        },
      }
    )
  }

  const handleResend = () => {
    if (resendDisabled || !currentSessionId) return
    setServerError(null)
    resendOtp.mutate(
      { sessionId: currentSessionId },
      {
        onSuccess: (data) => {
          setCode('')
          setCurrentSessionId(data.sessionId)
          setResendCooldownUntilMs(Date.now() + RESEND_COOLDOWN_SECONDS * 1000)
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            const lockout = lockoutFromOtpApiError(error)
            if (lockout && (error.isForbidden || error.isRateLimited)) {
              if (error.isForbidden) {
                setLockoutUntilMs(lockout.lockedUntilMs)
                setServerError(error.message || LOCKOUT_MESSAGE)
                setCode('')
                return
              }
              setResendCooldownUntilMs(
                Date.now() + lockout.retryAfterSeconds * 1000
              )
              setServerError(error.message)
              return
            }
          }
          if (isNetworkFailureError(error) || isOtpDeliveryFailure(error)) {
            showToast(resolveOtpSendFailureMessage(error), 'error')
            return
          }
          if (error instanceof ApiError) {
            setServerError(resolveOtpInlineErrorMessage(error))
          }
        },
      }
    )
  }

  return (
    <AuthCard>
      <Box
        sx={{
          width: 64,
          height: 64,
          bgcolor: '#e6f5fc',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <RiShieldKeyholeLine size={32} color={palettePrimaryMain} aria-hidden />
      </Box>

      <Typography component="h1" sx={{ ...headingSx, mt: '22px' }}>
        Verify Identity
      </Typography>
      <Typography sx={{ ...subtitleSx, mt: '6px' }}>
        {maskedEmail
          ? `Enter the 6-digit code sent to ${maskedEmail}`
          : 'Enter the 6-digit code sent to your email'}
      </Typography>

      {isLockedOut && lockoutUntilMs != null && (
        <LockoutCountdown
          lockedUntilMs={lockoutUntilMs}
          message={serverError ?? LOCKOUT_MESSAGE}
        />
      )}

      <Box sx={{ mt: 3, width: '100%' }}>
        <OtpInput
          value={code}
          onChange={setCode}
          disabled={isLockedOut || verifyOtp.isPending}
          error={!!serverError && !isLockedOut}
        />
        {serverError && !isLockedOut && (
          <Typography
            role="alert"
            data-testid="force-reset-otp-error"
            sx={{ ...loginFieldErrorSx, textAlign: 'center', mt: '8px' }}
          >
            {serverError}
          </Typography>
        )}
      </Box>

      <Button
        type="button"
        variant="contained"
        fullWidth
        disableElevation
        disabled={code.length !== 6 || isLockedOut || verifyOtp.isPending}
        onClick={handleVerify}
        data-testid="force-reset-otp-verify-button"
        sx={{ ...primaryButtonSx, mt: 3 }}
      >
        {verifyOtp.isPending ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Verify'
        )}
      </Button>

      <Button
        type="button"
        variant="text"
        onClick={handleResend}
        disabled={resendDisabled}
        data-testid="force-reset-otp-resend-button"
        sx={{ mt: 2, textTransform: 'none', color: palettePrimaryMain }}
      >
        {resendRemaining > 0
          ? `Resend code in ${resendRemaining}s`
          : 'Resend code'}
      </Button>

      <BackToLoginButton />
    </AuthCard>
  )
}
